import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import { CallStatus, LinkedAccountStatusEnum, OrderSideEnum, OrderStatusEnum, PaymentMethodEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { TradingAccountStatusesEnum } from '../../lib/models/crm/types';
import { MarketState } from '../../lib/models/market-data/types';
import { RootState } from '../../lib/store';
import { refreshPayments } from '../../lib/store/common-actions';
import { CRMCall } from '../../lib/store/crm/types';
import {
  closeCardTransferStatus,
  getCardTransferStatus,
  getOwnerTradingAccounts,
  setPaymentStatusUIState,
} from '../../lib/store/payment';
import { PaymentCall, RefreshPaymentsPayload } from '../../lib/store/payment/types';
import {
  getWalletTotalCalculatedCash,
  isUserForVerificationStatus,
  isUserVerifiedStatus,
} from '../../lib/store/selectors';
import { resetRequestStatus } from '../../lib/store/trading/index';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import { isMyPortalTrade } from '../../lib/util/auth/helpers';
import { formatNumber } from '../../lib/util/DataHelpers';
import { isCallStatusError, isCallStatusPendingOrBigger, isCallStatusReady, isCallStatusUIDone } from '../../lib/util/error-handling/StatusByCallHelpers';
import { usePaymentFxOrders } from '../../lib/util/hooks/wallet/PaymentAndFxHook';
import { manageSubscriptions } from '../../lib/util/TradingHelpers';

import DepositIcon from '../../assets/img/wallet/icon-deposit_mob.svg';
import ExchangeIcon from '../../assets/img/wallet/icon-exchange_mob.svg';
import WithdrawIcon from '../../assets/img/wallet/icon-withdraw_mob.svg';
import { BaseModal, ClickableImage, Preloader } from '../../components';
import { useBrowserWidth, useIsMobile } from '../../hooks';
import { MAX_NUMBER_REQUESTS } from '../../util/constants';
import { UI_ROUTES } from '../../util/routes';

import CurrencyHeader from './components/CurrencyHeader/CurrencyHeader';
import ResultModal from './components/ResultModal/ResultModal';
import CardTransferModal, { Props as CardTransferModalProps } from './modal/cardTransfer/CardTransferModal';
import DepositSlipModal from './modal/depositSlipModal/DepositSlipModal';
import FXOrderModal from './modal/FXOrderModal/FXOrderModal';
import InternalTransferModal from './modal/InternalTransferModal/InternalTransferModal';
import NoVerifiedUserModal from './modal/noActivatedUserModal/NoActivatedUserModal';
import BasicModal from './modal/paymentsModal/BasicModal';
import MyPortalDepositOptionsPopup from './MyPortalDepositOptionsPopup/MyPortalDepositOptionsPopup';
import Transactions from './transactions/Transactions';
import { XL_BREAKPOINT_WIDTH } from './constants';
import LinkedAccountCard from './LinkedAccountCard';

import styles from './Funds.module.scss';

type Props = {} & ReduxProps;

const Funds = (props: Props) => {
  const {
    transactions,
    lastTransaction,
    linkedAccounts,
    calculatedCash,
    webLastCardTransfer,
    cardTransferSuccessData,
    cardTransferFailData,
    individual,
    statusByCall,
    callsForRefresh,
    isPaymentsReady,
    requestStatus,
    tradingAccounts,
    accountStatus,
    transferStatusError,
    transferStatusReady,
    refreshPaymentsData,
    setPaymentUIDone,
    getCardDepositStatus,
    closeCardTransfer,
    hasBankAccount,
    customRefreshPayments,
    getOwnerTradingAccountsData,
    resetOrderRequestStatus,
  } = props;
  const isMobile = useIsMobile();
  const history = useHistory();
  const location = useLocation();
  const { resetFxTransactions } = usePaymentFxOrders();
  const { marketState } = MarketStateCache.use();
  const { browserWidthSize } = useBrowserWidth();
  const isUserVerified = isUserVerifiedStatus(individual);
  const isUserInPendingVerification = isUserForVerificationStatus(individual);

  const locationState: any = history.location.state;
  const isCashLoaded = isCallStatusUIDone(PaymentCall.cashCalculated, statusByCall);

  const [bankAccountStatus, setBankAccountStatus] = useState(
    {
      values: {
        hasLinkedAccounts: true,
        isPendingLinkedAccounts: false,
        bankAccountStatusTitle: lang.mobileFundsScreenModalTitle(),
        bankAccountStatusMessage: '',
      },
    },
  );
  const [counterRequest, setCounterRequest] = useState<number>(MAX_NUMBER_REQUESTS);
  const [isLoadedWalletPageFromDskPage, setIsLoadedWalletPageFromDskPage] = useState<boolean>(false);
  const [showModalPayments, setShowModalPayments] = useState<boolean>(false);
  const [showModalDepositSlip, setShowModalDepositSlip] = useState<boolean>(false);
  const [showModalTransferSlip, setShowModalTransferSlip] = useState<boolean>(false);
  const [currentAvailbleCash, setCurrentAvailbleCash] = useState<number>(0);
  const [showExchangeModal, setShowExchangeModal] = useState<boolean>(false);
  const [isOrderCreated, setIsOrderCreated] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(0);
  const [orderSide, setOrderSide] = useState<OrderSideEnum>(OrderSideEnum.Buy);
  const [cardTransferModalData, setCardTransferModalData] = useState<CardTransferModalProps>({
    cardPaymentStatus: 0,
  } as any);
  const [showFXOrderResult, setShowFXOrderResult] = useState<boolean>(false);
  const [showMyPortalDepositOptions, setShowMyPortalDepositOptions] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleModalPayments = () => {
    setShowModalPayments(!showModalPayments);
  };

  const handleConfirmDepositSlipModal = () => {
    setShowModalDepositSlip(false);
  };

  const handleCloseInternalTransferModal = () => {
    setShowModalTransferSlip(false);
    customRefreshPayments({
      [PaymentCall.getPaymentsAll]: true,
      [PaymentCall.getAvailableCash]: true,
    });
  };

  useEffect(() => {
    if (
      (locationState?.hasFXOrder || isOrderCreated)
    && ![OrderStatusEnum.Initial, OrderStatusEnum.New].includes(requestStatus)
    ) {
      setShowFXOrderResult(true);
      setIsOrderCreated(false);
    }
  }, [isOrderCreated, locationState?.hasFXOrder, requestStatus]);

  const navigateToDepositScreen = (option?: number) => {
    let isLastCardPaymentMethod = lastTransaction?.payment_method === PaymentMethodEnum.Card;
    let depositUrl = UI_ROUTES.funds + UI_ROUTES.enterAmount;

    if (option === PaymentMethodEnum.Card) {
      isLastCardPaymentMethod = true;
    }

    if (option === PaymentMethodEnum.BankTransfer) {
      isLastCardPaymentMethod = false;
      if (!hasBankAccount) {
        history.push(UI_ROUTES.funds + UI_ROUTES.addYourBankAccount);
        return;
      }
    }

    if (option === PaymentMethodEnum.InternalTransfer) {
      depositUrl = UI_ROUTES.funds + UI_ROUTES.internalTransfer;
    }

    history.push({
      pathname: depositUrl,
      state: {
        previousPage: UI_ROUTES.funds,
        target: lang.mobileBankDialogTitle(),
        option,
        isLastCardPaymentMethod },
    });
  };

  const handleBtnFund = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMyPortalTrade()) {
      setAnchor(event.currentTarget);
      setShowMyPortalDepositOptions(!showMyPortalDepositOptions);
    } else {
      navigateToDepositScreen();
    }
  };

  const handleBtnWithdraw = () => {
    if (linkedAccounts.length === 0) {
      setBankAccountStatus(prevState => ({
        ...prevState,
        values: {
          ...prevState.values,
          hasLinkedAccounts: false,
          isPendingLinkedAccounts: false,
          bankAccountStatusMessage: lang.mobileFundsScreenAddBankAccModal(),
        },
      }));
      setShowModalPayments(true);
      return;
    }
    const hasActiveLinkedAccounts = linkedAccounts.find(linkedAccount =>
      linkedAccount.status === LinkedAccountStatusEnum.Active);

    if (!hasActiveLinkedAccounts) {
      setBankAccountStatus(prevState => ({
        ...prevState,
        values: {
          ...prevState.values,
          hasLinkedAccounts: false,
          isPendingLinkedAccounts: true,
          bankAccountStatusMessage: lang.mobileFundsScreenBankAccPendingModal(),
        },
      }));
      setShowModalPayments(true);
    } else {
      history.push({
        pathname: UI_ROUTES.funds + UI_ROUTES.withdrawalAmount,
        state: {
          previousPage: UI_ROUTES.funds },
      });
    }
  };

  if (location.search.length > 0 && !isLoadedWalletPageFromDskPage) {
    setIsLoadedWalletPageFromDskPage(true);
  }

  useEffect(() => {
    manageSubscriptions('funds', null, false, true);
  }, []);

  useEffect(() => {
    if (locationState?.target === lang.mobileDepositSlipDialogTitle()) {
      setShowModalDepositSlip(true);
      history.replace({ ...history.location, state: '' });
    }

    if (locationState?.amount && (transferStatusError || transferStatusReady)) {
      setShowModalTransferSlip(true);
    }
  }, [locationState, setShowModalDepositSlip, history, transferStatusError, transferStatusReady]);

  useEffect(() => {
    const lastPaymentStatus = cardTransferSuccessData?.status;

    if (cardTransferModalData.cardPaymentStatus !== 0) return;

    if (lastPaymentStatus) {
      const amount = webLastCardTransfer?.amount;
      setCardTransferModalData({
        cardPaymentStatus: lastPaymentStatus,
        amount,
      });
      closeCardTransfer();
    }
  }, [
    history,
    webLastCardTransfer,
    transactions,
    setPaymentUIDone,
    cardTransferSuccessData,
    cardTransferFailData,
    closeCardTransfer,
    cardTransferModalData.cardPaymentStatus,
  ]);

  useEffect(() => {
    if (
      isUserVerified
      && location?.search?.includes('orderId=')
      && !isCallStatusPendingOrBigger(PaymentCall.getCardTransferStatus, statusByCall)
    ) {
      getCardDepositStatus();
      history.push(UI_ROUTES.funds);
    }
  }, [getCardDepositStatus, isUserVerified, location?.search, statusByCall, setPaymentUIDone, history]);

  useEffect(() => {
    if (callsForRefresh) {
      refreshPaymentsData();
    }
  }, [callsForRefresh, refreshPaymentsData]);

  useEffect(() => {
    if (isUserVerified) {
      customRefreshPayments({
        [PaymentCall.getPaymentsAll]: true,
        [PaymentCall.getAvailableCash]: true,
      });
    }
  }, [customRefreshPayments, isUserVerified]);

  useEffect(() => {
    if (isUserVerified && !tradingAccounts.length && counterRequest > 0) {
      getOwnerTradingAccountsData();
      setTimeout(() => { setCounterRequest(requestNum => requestNum - 1); }, 1000);
    } else if (!tradingAccounts.length && !counterRequest) {
      history.push({
        pathname: UI_ROUTES.error,
        state: { target: location.pathname, isErrorTradingAccounts: true, isLoadedWalletPageFromDskPage },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterRequest]);

  const handleBtnExchange = () => {
    if (isMobile && marketState !== MarketState.MarketClosed) {
      history.push(UI_ROUTES.funds + UI_ROUTES.fxOrder);
    } else {
      setShowExchangeModal(true);
    }
  };

  const closeFXModal = () => {
    setShowFXOrderResult(false);
    resetOrderRequestStatus();
    resetFxTransactions();
  };

  const isUserNotVerifiedAndPortalIsNotMyPortalTrade = !isUserVerified && !isMyPortalTrade();
  const myPortalTradePendingActivationPopup = isMyPortalTrade() && accountStatus !== TradingAccountStatusesEnum.Active;

  return (
    <>
      {(!tradingAccounts.length && counterRequest <= MAX_NUMBER_REQUESTS && !isMyPortalTrade())
        && <Preloader owner="Funds" text="" isFullScreen stylePreset="customOne" />}
      <div className={styles.wrapperLayout}>
        <div className={styles.wrapperFirstSection}>
          {!isMobile && (<h6 id="fundsTitle" className={styles.title}>{lang.fundsScreenTitle()}</h6>)}
          <div className={(isMobile ? styles.availableCashHeaderMob : styles.availableCashHeaderWeb)}>
            <CurrencyHeader
              currentAvailbleCash={currentAvailbleCash}
              setCurrentAvailableCash={(val) => setCurrentAvailbleCash(val)}
            />
            <div className={classNames(styles.cashOperations, { [styles.cashOperationsWeb]: !isMobile })}>
              <div>
                <ClickableImage src={DepositIcon} onClick={handleBtnFund} />
                <p>{lang.fundsScreenFundBtn()}</p>
                <MyPortalDepositOptionsPopup
                  isOpen={showMyPortalDepositOptions}
                  anchorEl={anchor}
                  handleSelect={(option) => { navigateToDepositScreen(option); }}
                  handleClose={() => { setShowMyPortalDepositOptions(false); }}
                />
              </div>
              <div>
                <ClickableImage
                  className={(
                    !isCashLoaded
                    || (Number(formatNumber(calculatedCash)) <= 0)
                    || isUserNotVerifiedAndPortalIsNotMyPortalTrade
                    || !currentAvailbleCash ? styles.disabledBtn : '')}
                  src={WithdrawIcon}
                  onClick={handleBtnWithdraw}
                />
                <p className={(!currentAvailbleCash ? styles.disabled : '')}>{lang.mobileFundsScreenWithdrawBtn()}</p>
              </div>
              <div>
                <ClickableImage
                  className={(!isCashLoaded || (Number(formatNumber(calculatedCash)) <= 0) ? styles.disabledBtn : '')}
                  src={ExchangeIcon}
                  onClick={handleBtnExchange}
                />
                <p className={(!calculatedCash ? styles.disabled : '')}>{lang.fundsScreenExchangeBtn()}</p>
              </div>
            </div>
          </div>
          { browserWidthSize < XL_BREAKPOINT_WIDTH && !isMobile
             && <LinkedAccountCard isUserVerified={isUserVerified} linkedAccounts={linkedAccounts} />}
          {
            isPaymentsReady && <Transactions isUserVerified={isUserVerified} browserWidthSize={browserWidthSize} />
          }
        </div>
        <div className={styles.wrapperSecondSection}>
          { browserWidthSize >= XL_BREAKPOINT_WIDTH
          && <LinkedAccountCard isUserVerified={isUserVerified} linkedAccounts={linkedAccounts} />}
        </div>
      </div>
      <BaseModal
        isOpen={showModalPayments
        || showModalDepositSlip
        || isUserNotVerifiedAndPortalIsNotMyPortalTrade}
        className={isUserNotVerifiedAndPortalIsNotMyPortalTrade ? styles.blurBackground : ''}
      >
        { showModalPayments && <BasicModal handleModal={handleModalPayments} infoModal={bankAccountStatus} />}
        { showModalDepositSlip && <DepositSlipModal handleModal={handleConfirmDepositSlipModal} />}
        {isUserNotVerifiedAndPortalIsNotMyPortalTrade && (
          <NoVerifiedUserModal isPendingVerification={isUserInPendingVerification} />
        )}
      </BaseModal>
      { myPortalTradePendingActivationPopup && (
        <BaseModal
          isOpen={myPortalTradePendingActivationPopup}
          className={isUserNotVerifiedAndPortalIsNotMyPortalTrade ? styles.blurBackground : ''}
        >
          <NoVerifiedUserModal isPendingVerification={isUserInPendingVerification} />
        </BaseModal>
      )}
      <FXOrderModal
        closeModal={() => setShowExchangeModal(false)}
        isOpen={showExchangeModal}
        setQuantity={(value => setQuantity(value))}
        setOrderSide={value => setOrderSide(value)}
        setIsOrderCreated={(value) => setIsOrderCreated(value)}
      />
      { cardTransferModalData.cardPaymentStatus > 0 && <CardTransferModal {...cardTransferModalData} /> }
      { isMobile && <div className={styles.mobileBottom} /> }
      {showFXOrderResult && (
      <ResultModal
        handleClose={closeFXModal}
        side={isMobile ? locationState?.orderSide : orderSide}
        quantity={isMobile ? locationState?.quantity : quantity}
      />
      )}
      {showModalTransferSlip && (
        <InternalTransferModal
          isSuccessful={locationState?.isSuccess}
          amount={locationState?.amount}
          onClose={handleCloseInternalTransferModal}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasBankAccount: state.payment.hasBankAccount,
  transactions: state.payment.paymentsAll.data,
  lastTransaction: state.payment.lastUserTransaction,
  linkedAccounts: state.payment.linkedAccounts,
  calculatedCash: getWalletTotalCalculatedCash(state),
  webLastCardTransfer: state.payment.webLastCardTransfer,
  cardTransferSuccessData: state.payment.lastCardTransfer.statusCheckData,
  cardTransferFailData: state.payment.lastCardTransfer.transferFailedData,
  individual: state.crm.individualExtendedInfo,
  callsForRefresh: state.payment.callsForRefresh,
  statusByCall: state.payment.statusByCall,
  tradingAccounts: state.payment.tradingAccounts,
  requestStatus: state.trading.requestStatus,
  accountStatus: state.crm.accountInfo[0]?.account_status ? state.crm.accountInfo[0].account_status : 0,
  isPaymentsReady: state.payment.statusByCall[PaymentCall.getPaymentsAll]?.status === CallStatus.READY,
  transferStatusReady: isCallStatusReady(CRMCall.internalTransfer, state.crm.statusByCall),
  transferStatusError: isCallStatusError(CRMCall.internalTransfer, state.crm.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  closeCardTransfer: () => dispatch(closeCardTransferStatus()),
  getCardDepositStatus: () => dispatch(getCardTransferStatus()),
  customRefreshPayments: (data: RefreshPaymentsPayload) => dispatch(refreshPayments(data)),
  refreshPaymentsData: () => dispatch(refreshPayments()),
  setPaymentUIDone: (call: PaymentCall, isDone: boolean = true) => dispatch(setPaymentStatusUIState({ call, isDone })),
  getOwnerTradingAccountsData: () => dispatch(getOwnerTradingAccounts()),
  resetOrderRequestStatus: () => dispatch(resetRequestStatus()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Funds);
